import { createStart, createMiddleware, createCsrfMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
import { describeError, toAppError } from "./lib/errors";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";

/**
 * Request-level error boundary.
 *
 * Catches anything thrown while a request is being handled and returns a
 * complete HTML document instead of a blank screen. Responses that already
 * carry a status code (redirects, `throw redirect(...)`, TanStack's own
 * HTTP errors) are re-thrown untouched: wrapping those in a 500 page would
 * turn a legitimate 401 or 404 into a server fault.
 */
const errorMiddleware = createMiddleware().server(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }

    const area = "request";
    const appError = toAppError(error, { area });

    // No user context is available here, so the raw cause is logged for the
    // operators and nothing technical is sent to the browser. `toAppError` only
    // stages raw text off the error object, so the view below cannot leak it
    // even by accident.
    console.error(`[${appError.code}] ${area}`, error);

    const view = describeError(appError, { isSuperadmin: false });

    return new Response(
      renderErrorPage({
        title: view.title,
        message: view.message,
        hint: view.hint,
      }),
      {
        status: 500,
        headers: { "content-type": "text/html; charset=utf-8" },
      },
    );
  }
});

/**
 * Cross-site request forgery guard for server functions.
 *
 * Server functions are POST endpoints that run with the caller's session, so a
 * third-party page must not be able to invoke them. The middleware validates
 * `Sec-Fetch-Site` / `Origin` / `Referer` on those requests only; the `filter`
 * limits it to `serverFn` calls so document and asset requests, which have no
 * cross-site risk, are untouched. The matching warning TanStack Start logs in
 * development is satisfied by this middleware - it is intentionally absent
 * from production builds, which is why it must be configured here rather than
 * suppressed.
 */
const csrfMiddleware = createCsrfMiddleware({
  filter: (ctx) => ctx.handlerType === "serverFn",
});

export const startInstance = createStart(() => ({
  functionMiddleware: [attachSupabaseAuth],
  requestMiddleware: [errorMiddleware, csrfMiddleware],
}));
